import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import {type NotificationDto} from '@onroadvantage/optix-safeandsound-openapi';

import {getNotificationIcon} from './notificationIcons';
import {hasNotificationLink} from '../../hooks/notifications/useNotificationsMenu';
import {formatRelativeTime} from '../../utils/formatRelativeTime';

interface NotificationListItemProps {
  notification: NotificationDto;
  onClick: (notification: NotificationDto) => void;
  onArchive: (id: string) => void;
}

export function NotificationListItem({
  notification,
  onClick,
  onArchive,
}: NotificationListItemProps): JSX.Element {
  const isUnread = notification.readAt == null;

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <IconButton
          aria-label="Dismiss notification"
          edge="end"
          size="small"
          onClick={(): void => {
            onArchive(notification.id);
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton
        alignItems="flex-start"
        onClick={(): void => {
          onClick(notification);
        }}
        sx={{pr: 6}}
      >
        <ListItemIcon sx={{minWidth: 40, mt: 0.5}}>
          <Badge variant="dot" color="error" invisible={!isUnread}>
            {getNotificationIcon(notification.type)}
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={notification.title}
          primaryTypographyProps={{
            fontWeight: isUnread ? 600 : 400,
            variant: 'body2',
          }}
          secondary={
            <>
              {notification.body}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{display: 'flex', alignItems: 'center', gap: 0.5}}
              >
                {formatRelativeTime(notification.createdAt)}
                {hasNotificationLink(notification) && (
                  <LinkIcon sx={{fontSize: 14}} aria-label="Has link" />
                )}
              </Typography>
            </>
          }
          secondaryTypographyProps={{variant: 'body2', component: 'span'}}
        />
      </ListItemButton>
    </ListItem>
  );
}
